import type { KeyboardEvent } from 'react';

interface ChatSearchBarProps {
  query: string;
  onQuery: (value: string) => void;
  /** 1-based place of the match being looked at, and how many there are. */
  position: number;
  total: number;
  onOlder: () => void;
  onNewer: () => void;
  onClose: () => void;
}

/** The search row under a chat header: the words, "2 of 5", and arrows to jump between matches. */
export function ChatSearchBar({ query, onQuery, position, total, onOlder, onNewer, onClose }: ChatSearchBarProps) {
  const searching = query.trim().length > 0;
  const keep = (e: { preventDefault: () => void }) => e.preventDefault(); // pressing an arrow keeps the keyboard up

  function onKeyDown(e: KeyboardEvent<HTMLInputElement>) {
    if (e.key === 'Enter') { e.preventDefault(); if (e.shiftKey) onNewer(); else onOlder(); }
    if (e.key === 'Escape') onClose();
  }

  const arrow = (label: string, icon: string, onClick: () => void) => (
    <button
      type="button"
      aria-label={label}
      disabled={total === 0}
      onMouseDown={keep}
      onClick={onClick}
      className="sk-icon-btn flex items-center justify-center rounded-full text-app-fg-2 disabled:opacity-30"
      style={{ width: 40, height: 40 }}
    >
      <i className={`ti ti-${icon} text-[length:calc(20px*var(--text-scale))]`} />
    </button>
  );

  return (
    <div className="shrink-0 flex items-center gap-1 px-2.5 py-1.5 border-b border-app-border" role="search">
      <div className="flex-1 min-w-0 flex items-center gap-2 rounded-xl bg-app-card border border-app-border px-3" style={{ height: 40 }}>
        <i className="ti ti-search text-app-muted text-[length:calc(16px*var(--text-scale))] shrink-0" />
        <input
          type="search"
          autoFocus
          value={query}
          onChange={(e) => onQuery(e.target.value)}
          onKeyDown={onKeyDown}
          placeholder="Search in conversation"
          aria-label="Search in conversation"
          enterKeyHint="search"
          className="flex-1 min-w-0 bg-transparent outline-none text-app-fg placeholder:text-app-muted"
          style={{ fontSize: 'var(--fs-body)' }}
        />
      </div>
      <span className="text-footnote text-app-fg-2 whitespace-nowrap min-w-[56px] text-center" aria-live="polite">
        {searching ? (total > 0 ? `${position} of ${total}` : 'No matches') : ''}
      </span>
      {arrow('Older match', 'chevron-up', onOlder)}
      {arrow('Newer match', 'chevron-down', onNewer)}
    </div>
  );
}
